import type { ElkExtendedEdge, ElkNode } from 'elkjs/lib/elk-api'
import type { ProcessCanvasNodeDefinition, ProcessEdgeDefinition, ProcessNodeDefinition } from '@/frontend/features/Fde/types'

export const PROCESS_NODE_WIDTH = 248
export const PROCESS_EDGE_LABEL_WIDTH = 210

const BRANCH_TRANSITIONS = new Set(['exception', 'cancel'])
const BRANCH_LANE_GAP = 72
const BRANCH_NODE_GAP = 44

export function processNodeDimensions(node: ProcessNodeDefinition) {
  const hasContext = node.ontology_refs.length > 0
  const hasMetadata = node.ontology_refs.length > 0 || node.evidence_ids.length > 0
  const titleLines = node.label.trim().length > 28 ? 2 : 1
  const contentHeight = 48 + titleLines * 16 + (hasContext ? 34 : 0) + (hasMetadata ? 18 : 0)
  return { width: PROCESS_NODE_WIDTH, height: Math.max(94, contentHeight) }
}

export function processEdgeLabelDimensions(label: string) {
  const length = label.trim().length
  const width = Math.min(PROCESS_EDGE_LABEL_WIDTH, Math.max(72, 20 + length * 5.4))
  const charactersPerLine = Math.max(12, Math.floor((width - 16) / 5.4))
  const lines = Math.min(3, Math.max(1, Math.ceil(length / charactersPerLine)))
  return { width, height: 14 + lines * 14 }
}

let elkPromise: Promise<import('elkjs/lib/elk-api').ELK> | null = null

function elk() {
  elkPromise ||= import('elkjs/lib/elk.bundled.js').then(({ default: ELK }) => new ELK())
  return elkPromise
}

export function seedProcessFlow(nodes: ProcessNodeDefinition[]): ProcessCanvasNodeDefinition[] {
  return nodes.map((node, index) => ({
    ...node,
    action_type_id: node.action_type_id ?? null,
    position: { x: (index % 4) * 276, y: Math.floor(index / 4) * 128 },
  }))
}

export async function layoutProcessFlow(nodes: ProcessNodeDefinition[], edges: ProcessEdgeDefinition[]): Promise<ProcessCanvasNodeDefinition[]> {
  if (nodes.length === 0) return []
  const graph: ElkNode = {
    id: 'process',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': 'RIGHT',
      'elk.spacing.nodeNode': '44',
      'elk.spacing.edgeNode': '22',
      'elk.spacing.edgeEdge': '16',
      'elk.layered.spacing.nodeNodeBetweenLayers': '44',
      'elk.layered.spacing.edgeNodeBetweenLayers': '20',
      'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
      'elk.edgeRouting': 'ORTHOGONAL',
    },
    children: nodes.map((node) => ({ id: node.id, ...processNodeDimensions(node) })),
    edges: edges.map((edge): ElkExtendedEdge => {
      const label = edge.label?.trim() || edge.condition?.trim()
      return {
        id: edge.id,
        sources: [edge.source],
        targets: [edge.target],
        labels: label ? [ { id: `${edge.id}-label`, text: label, ...processEdgeLabelDimensions(label) } ] : undefined,
      }
    }),
  }
  const result = await (await elk()).layout(graph)
  const positions = new Map(result.children?.map((node) => [node.id, { x: node.x || 0, y: node.y || 0 }]))
  const incomingEdges = new Map(nodes.map((node) => [node.id, edges.filter((edge) => edge.target === node.id)]))
  const branchNodeIds = new Set(nodes.filter((node) => {
    const incoming = incomingEdges.get(node.id) || []
    return node.kind === 'gap' || (incoming.length > 0 && incoming.every((edge) => BRANCH_TRANSITIONS.has(edge.transition_kind)))
  }).map((node) => node.id))
  const mainBottom = Math.max(0, ...nodes.filter((node) => !branchNodeIds.has(node.id)).map((node) => {
    const position = positions.get(node.id) || { x: 0, y: 0 }
    return position.y + processNodeDimensions(node).height
  }))
  const branchRows: number[] = []
  nodes.filter((node) => branchNodeIds.has(node.id)).sort((left, right) => {
    return (positions.get(left.id)?.x || 0) - (positions.get(right.id)?.x || 0)
  }).forEach((node) => {
    const position = positions.get(node.id) || { x: 0, y: 0 }
    const dimensions = processNodeDimensions(node)
    const row = branchRows.findIndex((rightEdge) => position.x >= rightEdge + BRANCH_NODE_GAP)
    const rowIndex = row === -1 ? branchRows.length : row
    branchRows[rowIndex] = position.x + dimensions.width
    positions.set(node.id, {
      x: position.x,
      y: mainBottom + BRANCH_LANE_GAP + rowIndex * (dimensions.height + BRANCH_NODE_GAP),
    })
  })
  return nodes.map((node, index) => ({
    ...node,
    action_type_id: node.action_type_id ?? null,
    position: positions.get(node.id) || { x: (index % 4) * 276, y: Math.floor(index / 4) * 128 },
  }))
}
